import { getBcShopName } from '../helpers';
/**
 * BigCommerce Storefront API helpers for accessing cart and checkout data
 * These APIs are available when scripts run on the checkout page
 */
// Cache for the storefront token
let storefrontTokenCache = null;
/**
 * Fetch the BigCommerce Storefront API token from our backend
 * The token is cached for 5 minutes to avoid excessive API calls
 */
async function getStorefrontToken() {
    // Check cache first
    if (storefrontTokenCache && storefrontTokenCache.expiresAt > Date.now()) {
        return storefrontTokenCache.token;
    }
    try {
        const shopName = getBcShopName();
        const apiHost = process.env.VITE_REAL_ID_HOST || 'https://app.getverdict.com';
        const response = await fetch(`${apiHost}/api/bc/shops/${encodeURIComponent(shopName)}/storefront-token`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
            },
        });
        if (!response.ok) {
            console.debug('Real ID :: Failed to fetch storefront token:', response.status);
            return null;
        }
        const data = await response.json();
        if (data.token) {
            // Cache for 5 minutes
            storefrontTokenCache = {
                token: data.token,
                expiresAt: Date.now() + 5 * 60 * 1000,
            };
            return data.token;
        }
        return null;
    }
    catch (error) {
        console.debug('Real ID :: Error fetching storefront token:', error);
        return null;
    }
}
/**
 * Make a GraphQL request to BigCommerce Storefront API with proper authentication
 */
async function bcGraphqlRequest(query, variables = {}) {
    const token = await getStorefrontToken();
    const headers = {
        'Content-Type': 'application/json',
    };
    // Add authorization header if we have a token
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch('/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({ query, variables }),
    });
    if (!response.ok) {
        return { errors: [{ message: `HTTP ${response.status}` }] };
    }
    return response.json();
}
/**
 * Fetch current cart data from BigCommerce Storefront API
 */
export async function getBcCart() {
    try {
        const response = await fetch('/api/storefront/carts', {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
            },
        });
        if (!response.ok) {
            console.error('Real ID :: Failed to fetch BC cart:', response.status);
            return null;
        }
        const carts = await response.json();
        // Returns array of carts, we want the first one
        if (carts && carts.length > 0) {
            return carts[0];
        }
        return null;
    }
    catch (error) {
        console.error('Real ID :: Error fetching BC cart:', error);
        return null;
    }
}
/**
 * Fetch checkout data from BigCommerce Storefront API
 */
export async function getBcCheckout(cartId) {
    try {
        const response = await fetch(`/api/storefront/checkouts/${cartId}`, {
            credentials: 'include',
            headers: {
                'Accept': 'application/json',
            },
        });
        if (!response.ok) {
            console.error('Real ID :: Failed to fetch BC checkout:', response.status);
            return null;
        }
        return await response.json();
    }
    catch (error) {
        console.error('Real ID :: Error fetching BC checkout:', error);
        return null;
    }
}
/**
 * Check if cart total exceeds threshold
 */
export async function checkCartTotalTrigger(threshold) {
    const cart = await getBcCart();
    if (!cart) {
        console.debug('Real ID :: Cart total trigger - no cart found');
        return false;
    }
    const cartTotal = cart.cartAmount;
    const isTriggered = cartTotal >= threshold;
    console.debug(`Real ID :: Cart total trigger - total: $${cartTotal}, threshold: $${threshold}, triggered: ${isTriggered}`);
    return isTriggered;
}
/**
 * Check if any cart items belong to required categories
 */
export async function checkCategoryTrigger(requiredCategoryIds) {
    if (!requiredCategoryIds || requiredCategoryIds.length === 0) {
        console.debug('Real ID :: Category trigger - no required categories configured');
        return false;
    }
    const cart = await getBcCart();
    if (!cart) {
        console.debug('Real ID :: Category trigger - no cart found');
        return false;
    }
    // Get all product IDs from cart
    const allItems = [
        ...(cart.lineItems.physicalItems || []),
        ...(cart.lineItems.digitalItems || []),
    ];
    const productIds = allItems.map(item => item.productId);
    if (productIds.length === 0) {
        console.debug('Real ID :: Category trigger - no products in cart');
        return false;
    }
    console.debug(`Real ID :: Category trigger - checking products: ${productIds.join(', ')}`);
    console.debug(`Real ID :: Category trigger - required categories: ${requiredCategoryIds.join(', ')}`);
    // Fetch product details to get category information
    // We'll use the GraphQL Storefront API if available, otherwise fall back to checking
    // if categories are embedded in the cart line items
    // First, check if categories are already available in cart line items
    for (const item of allItems) {
        if (item.categoryIds && item.categoryIds.length > 0) {
            const matchingCategories = item.categoryIds.filter(catId => requiredCategoryIds.includes(catId));
            if (matchingCategories.length > 0) {
                console.debug(`Real ID :: Category trigger - product ${item.productId} (${item.name}) matches required categories: ${matchingCategories.join(', ')}`);
                return true;
            }
        }
    }
    // If categories aren't in cart data, try GraphQL API
    try {
        const categoryMatch = await checkProductCategoriesViaGraphQL(productIds, requiredCategoryIds);
        if (categoryMatch) {
            return true;
        }
    }
    catch (error) {
        console.debug('Real ID :: Category trigger - GraphQL fallback failed:', error);
    }
    console.debug('Real ID :: Category trigger - no products match required categories');
    return false;
}
/**
 * Use GraphQL Storefront API to check product categories
 */
async function checkProductCategoriesViaGraphQL(productIds, requiredCategoryIds) {
    // Build GraphQL query to fetch product categories
    const query = `
    query ProductCategories($productIds: [Int!]) {
      site {
        products(entityIds: $productIds, first: 50) {
          edges {
            node {
              entityId
              name
              categories {
                edges {
                  node {
                    entityId
                    name
                  }
                }
              }
            }
          }
        }
      }
    }
  `;
    try {
        const result = await bcGraphqlRequest(query, { productIds });
        if (result.errors) {
            console.debug('Real ID :: GraphQL errors:', result.errors);
            return false;
        }
        const products = result.data?.site?.products?.edges || [];
        for (const productEdge of products) {
            const product = productEdge.node;
            const categories = product.categories?.edges || [];
            for (const categoryEdge of categories) {
                const categoryId = categoryEdge.node.entityId;
                if (requiredCategoryIds.includes(categoryId)) {
                    console.debug(`Real ID :: Category trigger - product ${product.entityId} (${product.name}) is in required category ${categoryId} (${categoryEdge.node.name})`);
                    return true;
                }
            }
        }
        return false;
    }
    catch (error) {
        console.debug('Real ID :: GraphQL category check error:', error);
        return false;
    }
}
/**
 * Check if US shipping state triggers ID verification
 * Uses the same pattern as Shopify/WooCommerce with checkOnlyUsa, usStateSpecificity, requiredIdUsStates
 */
async function checkUsaStateTrigger(usStateSpecificity, requiredIdUsStates) {
    const cart = await getBcCart();
    if (!cart) {
        console.debug('Real ID :: USA state trigger - no cart found');
        return false;
    }
    const checkout = await getBcCheckout(cart.id);
    if (!checkout) {
        console.debug('Real ID :: USA state trigger - no checkout found');
        return false;
    }
    // Get shipping address from consignments
    const shippingAddress = checkout.consignments?.[0]?.shippingAddress;
    if (!shippingAddress) {
        console.debug('Real ID :: USA state trigger - no shipping address yet');
        // If no shipping address is entered yet, we can't evaluate this trigger
        // Return false to allow checkout to continue until address is entered
        return false;
    }
    // Check if US order
    const countryCode = shippingAddress.countryCode?.toUpperCase();
    if (countryCode !== 'US') {
        console.debug(`Real ID :: USA state trigger - not a US order (country: ${countryCode})`);
        return false;
    }
    // All USA states trigger
    if (usStateSpecificity === 'all_usa') {
        console.debug('Real ID :: USA state trigger - all_usa mode, triggered for US order');
        return true;
    }
    // Specific US states only
    if (usStateSpecificity === 'only_us_states' && requiredIdUsStates?.length) {
        const stateCode = shippingAddress.stateOrProvinceCode?.toUpperCase();
        const normalizedStates = requiredIdUsStates.map(s => s.toUpperCase());
        const isTriggered = normalizedStates.includes(stateCode);
        console.debug(`Real ID :: USA state trigger - state: ${stateCode}, ` +
            `required: ${normalizedStates.join(', ')}, triggered: ${isTriggered}`);
        return isTriggered;
    }
    console.debug('Real ID :: USA state trigger - no matching state specificity');
    return false;
}
/**
 * Create a cart metafield to store the check ID
 * Uses GraphQL Storefront API with token authentication
 * This allows server-side association of the check with the order via webhook
 */
export async function setCheckIdOnCartMetafield(cartId, checkId) {
    const mutation = `
    mutation CreateCartMetafield($cartId: String!, $key: String!, $value: String!) {
      cart {
        createCartMetafield(input: {
          cartEntityId: $cartId
          data: {
            key: $key
            value: $value
          }
        }) {
          metafield {
            id
            key
            value
          }
          errors {
            ... on Error {
              message
            }
          }
        }
      }
    }
  `;
    try {
        const result = await bcGraphqlRequest(mutation, {
            cartId,
            key: 'realid_check_id',
            value: checkId
        });
        if (result.errors || result.data?.cart?.createCartMetafield?.errors?.length > 0) {
            console.debug('Real ID :: Cart metafield errors:', result.errors || result.data?.cart?.createCartMetafield?.errors);
            return false;
        }
        console.debug('Real ID :: Cart metafield created successfully for check:', checkId);
        return true;
    }
    catch (error) {
        console.debug('Real ID :: Cart metafield creation error:', error);
        return false;
    }
}
/**
 * Evaluate all configured triggers for BigCommerce pre-checkout
 * Uses the same settings attributes as Shopify/WooCommerce for consistency:
 * - priceFloorEnabled, priceFloor (cart total)
 * - requiredIdCategoriesEnabled, requiredIdCategories (category trigger)
 * - checkOnlyUsa, usStateSpecificity, requiredIdUsStates (US state trigger)
 *
 * Returns true if any trigger is activated (OR logic)
 */
export async function evaluateBcPreCheckoutTriggers(shopSettings) {
    const triggeredBy = [];
    console.debug('Real ID :: Evaluating BC pre-checkout triggers with shared settings');
    // Check "verify all orders" trigger first - if enabled, always trigger
    if (shopSettings?.checkAllOrders) {
        console.debug('Real ID :: checkAllOrders enabled, triggering verification for all orders');
        return { triggered: true, triggeredBy: ['checkAllOrders'] };
    }
    // Check if any specific triggers are enabled
    const hasAnyTriggerEnabled = shopSettings?.priceFloorEnabled ||
        shopSettings?.requiredIdCategoriesEnabled ||
        shopSettings?.checkOnlyUsa;
    // If no specific triggers are configured, do NOT trigger (opt-in model)
    // Merchants must explicitly enable at least one trigger or use checkAllOrders
    if (!hasAnyTriggerEnabled) {
        console.debug('Real ID :: No triggers configured, skipping verification (opt-in required)');
        return { triggered: false, triggeredBy: [] };
    }
    // 1. Price floor trigger (cart total)
    if (shopSettings?.priceFloorEnabled && shopSettings?.priceFloor) {
        const threshold = parseFloat(shopSettings.priceFloor);
        if (!isNaN(threshold)) {
            const cartTotalTriggered = await checkCartTotalTrigger(threshold);
            if (cartTotalTriggered) {
                triggeredBy.push(`priceFloor (>= $${threshold})`);
            }
        }
    }
    // 2. Required ID categories trigger
    if (shopSettings?.requiredIdCategoriesEnabled &&
        shopSettings?.requiredIdCategories?.length > 0) {
        const categoryTriggered = await checkCategoryTrigger(shopSettings.requiredIdCategories);
        if (categoryTriggered) {
            triggeredBy.push(`requiredIdCategories (IDs: ${shopSettings.requiredIdCategories.join(', ')})`);
        }
    }
    // 3. US States trigger (checkOnlyUsa pattern)
    if (shopSettings?.checkOnlyUsa) {
        const stateTriggered = await checkUsaStateTrigger(shopSettings.usStateSpecificity, shopSettings.requiredIdUsStates);
        if (stateTriggered) {
            triggeredBy.push(`checkOnlyUsa (${shopSettings.usStateSpecificity || 'all_usa'})`);
        }
    }
    const triggered = triggeredBy.length > 0;
    console.debug(`Real ID :: BC pre-checkout triggers evaluation complete - triggered: ${triggered}, by: ${triggeredBy.join(', ') || 'none'}`);
    return { triggered, triggeredBy };
}
